import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {HttpService} from '@core/services';
import {ApiCollection, ApiResponse, FieldContext, RiskDriver} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {CompanyStateService} from '@core/states';
import {AuthStateService} from '@auth/states';

@Injectable({
  providedIn: 'root'
})
export class RiskDatasourceService {
  constructor(
    protected apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getDrivers(options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskDriver[]>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_DRIVERS, {pagination: 'false'});
      const headers = this.buildHeaders(options?.skipLoader);
      const result = await this.apiService.get<ApiCollection<RiskDriver>>(url, headers ? {headers} : undefined);
      return {success: true, result: this.apiService.normalizeCollection<RiskDriver>(result).member};
    } catch (error: any) {
      log.error('Error obteniendo la lista de drivers:', error);
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos procesar tu solicitud. Por favor intenta nuevamente más tarde.'
    };
  }

  async getContexts(code: string, mapping?: string, data?: {
    driver: { [_: string]: string },
    mapping: { [_: string]: string }
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<FieldContext>> {
    try {
      const params: { [_: string]: string } = {
        code, mapping_code: mapping || '',
        ...(data?.driver || {}),
        ...(data?.mapping || {}),
      };
      const url = GET_URL(ENDPOINTS.RISK_DRIVER_CONTEXT, params);
      const headers = this.buildHeaders(options?.skipLoader);
      const result = await this.apiService.get<any>(url, headers ? {headers} : undefined);
      return {success: true, result};
    } catch (error: any) {
      log.error('Error obteniendo el contexto del driver:', error);
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos procesar tu solicitud. Por favor intenta nuevamente más tarde.'
    };
  }

  async getDataSourceConfig(dataSourceId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<any>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_DATA_SOURCE_CONFIG, {dataSourceId});
      const headers = this.buildHeaders(options?.skipLoader);
      const result = await this.apiService.get<any>(url, headers ? {headers} : undefined);
      return {success: true, result};
    } catch (error: any) {
      log.error('Error obteniendo la configuracion de la fuente de datos:', error);
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos procesar tu solicitud. Por favor intenta nuevamente más tarde.'
    };
  }

  async createDataSourceConfig(payload: any, options?: {skipLoader?: boolean}): Promise<ApiResponse<any>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_DATA_SOURCE_CONFIG_CREATE);
      const headers = this.buildHeaders(options?.skipLoader);
      const result = await this.apiService.post<any>(url, payload, headers ? {headers} : undefined);
      return {success: true, result};
    } catch (error: any) {
      log.error('Error creando la configuracion de la fuente de datos:', error);
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos procesar tu solicitud. Por favor intenta nuevamente más tarde.'
    };
  }

  async saveDataSourceConfig(dataSourceId: string | number, payload: any, options?: {skipLoader?: boolean}): Promise<ApiResponse<any>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_DATA_SOURCE_CONFIG, {dataSourceId});
      const headers = this.buildHeaders(options?.skipLoader);
      const result = await this.apiService.post<any>(url, payload, headers ? {headers} : undefined);
      return {success: true, result};
    } catch (error: any) {
      log.error('Error guardando la configuracion de la fuente de datos:', error);
    }
    return {
      success: false,
      message: '🚫 Lo sentimos, no pudimos procesar tu solicitud. Por favor intenta nuevamente más tarde.'
    };
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }
}
